<mat-toolbar color="primary">
  <mat-toolbar-row class="container">
    <span>{{ 'modals.survey-report.title' | translate }}</span>
    <span class="modal-toolbar-spacer"></span>
    <button mat-icon-button class="ml-auto close-toolbar-btn" type="button" (click)="cancel()"
      [matTooltip]="'generic.buttons.close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </mat-toolbar-row>
</mat-toolbar>

<div class="survey-report-modal">
  <mat-tab-group mat-stretch-tabs="false" animationDuration="0ms">
    <mat-tab [label]="'modals.survey-report.tab-content' | translate">
      <div class="tab-body">
        <h6 class="section-label">{{ 'modals.survey-report.questions-section' | translate }}</h6>
        <p class="small text-muted mb-2">{{ 'modals.survey-report.typologies-hint' | translate }}</p>
        <div class="typology-chips mb-2">
          @for (entry of availableTypologies; track entry.typology) {
            <button type="button" class="typology-chip" [class.selected]="selectedTypologies.has(entry.typology)" (click)="toggleTypology(entry.typology)">
              <mat-icon>{{ selectedTypologies.has(entry.typology) ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              {{ typologyKey(entry.typology) | translate }} ({{ entry.count }})
            </button>
          }
        </div>
        @if (counts.no_answers > 0) {
          <mat-checkbox color="primary" [(ngModel)]="options.skip_unanswered" name="skip_unanswered">
            {{ 'modals.survey-report.skip-unanswered' | translate: { count: counts.no_answers } }}
          </mat-checkbox>
        }

        <h6 class="section-label mt-4">{{ 'modals.survey-report.slides-section' | translate }}</h6>
        <div class="option-grid">
          @for (option of slideOptions; track option) {
            <mat-checkbox color="primary" [(ngModel)]="options[option]" [name]="option">
              <span class="option-title">{{ 'modals.survey-report.option-' + option | translate }}</span>
              <span class="option-hint">{{ 'modals.survey-report.option-' + option + '-hint' | translate }}</span>
            </mat-checkbox>
          }
        </div>

        <h6 class="section-label mt-4">{{ 'modals.survey-report.final-decisions-section' | translate }}</h6>
        <mat-slide-toggle color="primary" [(ngModel)]="options.use_final_decisions" name="use_final_decisions" (ngModelChange)="onUseFinalDecisionsChange()">
          {{ 'modals.survey-report.option-use_final_decisions' | translate }}
        </mat-slide-toggle>
        <p class="small text-muted mt-2 mb-2">{{ 'modals.survey-report.option-use_final_decisions-hint' | translate }}</p>
        @if (options.use_final_decisions && missingFinalDecisions.length > 0) {
          <div class="alert alert-warning small mb-0" role="alert">
            <div class="d-flex align-items-center font-weight-bold mb-1">
              <mat-icon class="mr-2">warning_amber</mat-icon>
              {{ 'modals.survey-report.missing-final-decisions' | translate: { count: missingFinalDecisions.length } }}
            </div>
            <div class="mb-2">{{ 'modals.survey-report.missing-final-decisions-hint' | translate }}</div>
            <ul class="mb-0 pl-3 overflow-auto" style="max-height: 160px">
              @for (question of missingFinalDecisions; track question.id) {
                <li class="text-truncate" [matTooltip]="question.question">
                  {{ 'modals.survey-report.question-number' | translate: { n: question.number } }} · {{ question.question }}
                </li>
              }
            </ul>
          </div>
        }
      </div>
    </mat-tab>

    <mat-tab [label]="'modals.survey-report.tab-style' | translate">
      <div class="tab-body">
        <div class="row">
          <div class="col-md-4">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'modals.survey-report.language' | translate }}</mat-label>
              <mat-select [(ngModel)]="options.language" name="report_language">
                <mat-option value="en">English</mat-option>
                <mat-option value="it">Italiano</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="col-md-8">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'modals.survey-report.report-title' | translate }}</mat-label>
              <input matInput maxlength="150" name="report_title" [(ngModel)]="options.title" [placeholder]="data.surveyTitle || ''">
            </mat-form-field>
          </div>
        </div>
        <mat-form-field appearance="outline" class="w-100 mt-3" subscriptSizing="dynamic">
          <mat-label>{{ 'modals.survey-report.subtitle' | translate }}</mat-label>
          <input matInput maxlength="200" name="report_subtitle" [(ngModel)]="options.subtitle">
        </mat-form-field>
        <mat-form-field appearance="outline" class="w-100 mt-3" subscriptSizing="dynamic">
          <mat-label>{{ 'modals.survey-report.footer' | translate }}</mat-label>
          <input matInput maxlength="120" name="report_footer" [(ngModel)]="options.footer_text" [placeholder]="'modals.survey-report.footer-placeholder' | translate">
        </mat-form-field>

        <h6 class="section-label mt-4">{{ 'modals.survey-report.accent-color' | translate }}</h6>
        <div class="color-swatches">
          @for (color of accentColors; track color.id) {
            <button type="button" class="color-swatch" [class.selected]="options.accent_color === color.id" [style.background]="color.hex"
              [matTooltip]="'modals.survey-report.color-' + color.id | translate" (click)="setAccent(color.id)">
              @if (options.accent_color === color.id) {
                <mat-icon>check</mat-icon>
              }
            </button>
          }
        </div>
        <p class="small text-muted mt-3 mb-0">{{ 'modals.survey-report.language-note' | translate }}</p>
      </div>
    </mat-tab>

    <mat-tab>
      <ng-template mat-tab-label>
        {{ 'modals.survey-report.tab-ai' | translate }}
        @if (options.include_ai_analysis && analysesIncomplete) {
          <span class="tab-badge">{{ missingCount + outdatedCount }}</span>
        }
      </ng-template>
      <div class="tab-body">
        <mat-slide-toggle color="primary" [(ngModel)]="options.include_ai_analysis" name="include_ai_analysis">
          {{ 'modals.survey-report.include-ai' | translate }}
        </mat-slide-toggle>
        <p class="small text-muted mt-2">{{ 'modals.survey-report.include-ai-hint' | translate }}</p>

        @if (options.include_ai_analysis) {
          <h6 class="section-label mt-3">{{ 'modals.survey-report.ai-type' | translate }}</h6>
          <div class="choice-cards mb-3">
            @for (type of aiTypes; track type) {
              <button type="button" class="choice-card" [class.selected]="options.ai_analysis_type === type" (click)="setAiType(type)">
                <span class="option-title">{{ 'modals.survey-report.ai-type-' + type | translate }}</span>
                <span class="option-hint">{{ 'modals.survey-report.ai-type-' + type + '-hint' | translate }}</span>
              </button>
            }
          </div>

          @if (canChooseLayout) {
            <h6 class="section-label">{{ 'modals.survey-report.ai-layout' | translate }}</h6>
            <div class="layout-note mb-2">
              <mat-icon class="mr-2">info</mat-icon>
              <span>{{ 'modals.survey-report.ai-layout-note' | translate }}</span>
            </div>
            <div class="choice-cards mb-3">
              <button type="button" class="choice-card" [class.selected]="options.ai_analysis_layout === 'side'" (click)="setLayout('side')">
                <span class="option-title">{{ 'modals.survey-report.ai-layout-side' | translate }}</span>
                <span class="option-hint">{{ 'modals.survey-report.ai-layout-side-hint' | translate }}</span>
              </button>
              <button type="button" class="choice-card" [class.selected]="options.ai_analysis_layout === 'separate'" (click)="setLayout('separate')">
                <span class="option-title">{{ 'modals.survey-report.ai-layout-separate' | translate }}</span>
                <span class="option-hint">{{ 'modals.survey-report.ai-layout-separate-hint' | translate }}</span>
              </button>
            </div>
          }

          @if (loadingAnalysis) {
            <mat-progress-bar mode="indeterminate" class="mb-3"></mat-progress-bar>
          }
          <div class="ai-summary mb-3">
            @for (status of summaryStatuses; track status) {
              @if (counts[status] > 0) {
                <span class="ai-chip" [class]="'ai-chip ai-chip-' + status">
                  {{ 'modals.survey-report.status-' + status | translate }}: {{ counts[status] }}
                </span>
              }
            }
          </div>

          @if (!analysesIncomplete) {
            <p class="small mb-2">{{ 'modals.survey-report.all-ready' | translate }}</p>
          }
          @if (selectableQuestions.length > 0) {
            <mat-radio-group class="report-options" [(ngModel)]="mode" name="report_mode">
              @if (missingCount > 0) {
                <mat-radio-button value="missing" color="primary">
                  <span class="option-title">{{ 'modals.survey-report.option-missing' | translate: { count: missingCount } }}</span>
                  <span class="option-hint">{{ 'modals.survey-report.option-missing-hint' | translate }}</span>
                </mat-radio-button>
              }
              @if (outdatedCount > 0) {
                <mat-radio-button value="update" color="primary">
                  <span class="option-title">{{ 'modals.survey-report.option-update' | translate: { count: outdatedCount + missingCount } }}</span>
                  <span class="option-hint">{{ 'modals.survey-report.option-update-hint' | translate: { outdated: outdatedCount } }}</span>
                </mat-radio-button>
              }
              @if (regenerableAnalysesCount > 0) {
                <mat-radio-button value="regenerate" color="primary">
                  <span class="option-title">{{ 'modals.survey-report.option-regenerate' | translate: { count: selectableQuestions.length } }}</span>
                  <span class="option-hint">{{ 'modals.survey-report.option-regenerate-hint' | translate }}</span>
                </mat-radio-button>
              }
              @if (selectableQuestions.length > 0) {
                <mat-radio-button value="selected" color="primary">
                  <span class="option-title">{{ 'modals.survey-report.option-selected' | translate }}</span>
                  <span class="option-hint">{{ 'modals.survey-report.option-selected-hint' | translate }}</span>
                </mat-radio-button>
              }
              <mat-radio-button value="now" color="primary">
                <span class="option-title">{{ 'modals.survey-report.option-now' | translate }}</span>
                <span class="option-hint">{{ 'modals.survey-report.option-now-hint' | translate }}</span>
              </mat-radio-button>
            </mat-radio-group>

            @if (mode === 'selected') {
              <div class="question-picker mt-3">
                <div class="picker-header">
                  <mat-checkbox color="primary" [checked]="allSelected" [indeterminate]="selectedAnalysesCount > 0 && !allSelected" (change)="toggleAll()">
                    {{ 'modals.survey-report.select-all' | translate: { selected: selectedAnalysesCount, total: selectableQuestions.length } }}
                  </mat-checkbox>
                </div>
                <div class="picker-list">
                  @for (question of selectableQuestions; track question.id) {
                    <div class="picker-row">
                      <mat-checkbox color="primary" [checked]="selectedIds.has(question.id)" (change)="toggleQuestion(question.id)">
                        <span class="picker-question" [matTooltip]="plainText(question.question)">{{ plainText(question.question) }}</span>
                      </mat-checkbox>
                      <span class="ai-chip ai-chip-{{ question.ai_status }}" [matTooltip]="question.error_message || ''">
                        @if (question.ai_status === 'completed') {
                          {{ 'modals.survey-report.status-completed-regenerate' | translate }}
                        } @else if (question.ai_status === 'failed') {
                          {{ 'modals.survey-report.failed-' + (question.error_reason || 'error') | translate }}
                        } @else if (question.ai_status === 'outdated') {
                          {{ 'modals.survey-report.outdated-detail' | translate: { analysed: question.analysed_answers_count, total: question.answers_count } }}
                        } @else {
                          {{ 'modals.survey-report.status-' + question.ai_status | translate }}
                        }
                      </span>
                    </div>
                  }
                </div>
              </div>
            }

            @if (mode !== 'now' && processingCount > 0) {
              <p class="small text-muted mt-3 mb-0">{{ 'modals.survey-report.processing-note' | translate: { count: processingCount } }}</p>
            }
            @if (counts.failed > 0) {
              <p class="small text-muted mt-2 mb-0">{{ 'modals.survey-report.failed-note' | translate: { count: counts.failed } }}</p>
            }
          }
        }
      </div>
    </mat-tab>
    <mat-tab>
      <ng-template mat-tab-label>
        {{ 'modals.survey-report.tab-revisions' | translate }}
        @if (options.include_revised_statements && generatableRevisions.length > 0) {
          <span class="tab-badge">{{ generatableRevisions.length }}</span>
        }
      </ng-template>
      <div class="tab-body">
        <mat-slide-toggle color="primary" [(ngModel)]="options.include_revised_statements" name="include_revised_statements">
          {{ 'modals.survey-report.option-include_revised_statements' | translate }}
        </mat-slide-toggle>
        <p class="small text-muted mt-2">{{ 'modals.survey-report.option-include_revised_statements-hint' | translate }}</p>

        @if (options.include_revised_statements) {
          @if (loadingRevisions) {
            <mat-progress-bar mode="indeterminate" class="mb-3"></mat-progress-bar>
          } @else if (!revision) {
            <p class="small text-muted mb-0">{{ 'modals.survey-report.revisions-unavailable' | translate }}</p>
          } @else if (revisionQuestions.length === 0) {
            <p class="small mb-0">{{ 'modals.survey-report.revisions-none' | translate }}</p>
          } @else {
            <div class="ai-summary mb-3">
              @for (status of revisionStatuses; track status) {
                @if (revisionCount(status) > 0) {
                  <span class="ai-chip revision-chip-{{ status }}">
                    {{ 'modals.survey-report.revision-status-' + status | translate }}: {{ revisionCount(status) }}
                  </span>
                }
              }
            </div>

            @if (!revisionsPending) {
              <p class="small mb-2">{{ 'modals.survey-report.revisions-all-ready' | translate }}</p>
            }
            @if (revisionChoicesAvailable) {
              <mat-radio-group class="report-options" [(ngModel)]="revisionMode" name="revision_mode">
                @if (generatableRevisions.length > 0) {
                  <mat-radio-button value="missing" color="primary">
                    <span class="option-title">{{ 'modals.survey-report.revision-option-missing' | translate: { count: generatableRevisions.length } }}</span>
                    <span class="option-hint">{{ 'modals.survey-report.revision-option-missing-hint' | translate }}</span>
                  </mat-radio-button>
                } @else if (revisionCount('processing') > 0) {
                  <mat-radio-button value="missing" color="primary">
                    <span class="option-title">{{ 'modals.survey-report.revision-option-wait' | translate: { count: revisionCount('processing') } }}</span>
                    <span class="option-hint">{{ 'modals.survey-report.revision-option-wait-hint' | translate }}</span>
                  </mat-radio-button>
                }
                @if (regenerableRevisions.length > 0) {
                  <mat-radio-button value="regenerate" color="primary">
                    <span class="option-title">{{ 'modals.survey-report.revision-option-regenerate' | translate: { count: selectableRevisions.length } }}</span>
                    <span class="option-hint">{{ 'modals.survey-report.revision-option-regenerate-hint' | translate }}</span>
                  </mat-radio-button>
                }
                @if (selectableRevisions.length > 0) {
                  <mat-radio-button value="selected" color="primary">
                    <span class="option-title">{{ 'modals.survey-report.revision-option-selected' | translate }}</span>
                    <span class="option-hint">{{ 'modals.survey-report.revision-option-selected-hint' | translate }}</span>
                  </mat-radio-button>
                }
                <mat-radio-button value="now" color="primary">
                  <span class="option-title">{{ 'modals.survey-report.revision-option-now' | translate }}</span>
                  <span class="option-hint">{{ 'modals.survey-report.revision-option-now-hint' | translate }}</span>
                </mat-radio-button>
              </mat-radio-group>

              @if (revisionMode === 'selected' && selectableRevisions.length > 0) {
                <div class="question-picker mt-3">
                  <div class="picker-header">
                    <mat-checkbox color="primary" [checked]="allRevisionsSelected" [indeterminate]="selectedRevisionCount > 0 && !allRevisionsSelected" (change)="toggleAllRevisions()">
                      {{ 'modals.survey-report.select-all' | translate: { selected: selectedRevisionCount, total: selectableRevisions.length } }}
                    </mat-checkbox>
                  </div>
                  <div class="picker-list">
                    @for (question of selectableRevisions; track question.id) {
                      <div class="picker-row">
                        <mat-checkbox color="primary" [checked]="selectedRevisionIds.has(question.id)" (change)="toggleRevision(question.id)">
                          <span class="picker-question" [matTooltip]="plainText(question.question)">{{ plainText(question.question) }}</span>
                        </mat-checkbox>
                        <span class="picker-chips">
                          <span class="ai-chip revision-chip-expected">{{ 'modals.survey-report.revision-expected-' + question.expected | translate }}</span>
                          <span class="ai-chip revision-chip-{{ question.revision_status }}"
                            [matTooltip]="question.mismatch ? ('modals.survey-report.revision-mismatch' | translate) : (question.error_message || '')">
                            {{ 'modals.survey-report.revision-status-' + question.revision_status | translate }}
                          </span>
                        </span>
                      </div>
                    }
                  </div>
                </div>
              }

              @if (effectiveRevisionMode !== 'now' && generatableRevisions.length > 0 && revisionCount('processing') > 0) {
                <p class="small text-muted mt-3 mb-0">{{ 'modals.survey-report.revision-processing-note' | translate: { count: revisionCount('processing') } }}</p>
              }
              @if (effectiveRevisionMode !== 'now') {
                <p class="small text-muted mt-2 mb-0">{{ 'modals.survey-report.revision-analysis-note' | translate }}</p>
              }

              <mat-checkbox class="d-block mt-3" color="primary" [(ngModel)]="options.revised_statements_placeholder" name="revised_statements_placeholder">
                <span class="option-title">{{ 'modals.survey-report.option-revised_statements_placeholder' | translate }}</span>
                <span class="option-hint">{{ 'modals.survey-report.option-revised_statements_placeholder-hint' | translate }}</span>
              </mat-checkbox>
            }
          }
        }
      </div>
    </mat-tab>
  </mat-tab-group>

  <div class="report-summary">
    <mat-icon class="mr-2">slideshow</mat-icon>
    <span>{{ 'modals.survey-report.summary' | translate: { count: includedQuestionsCount } }}</span>
    @if (effectiveMode !== 'now' || effectiveRevisionMode !== 'now') {
      <span class="ml-1">{{ 'modals.survey-report.summary-wait' | translate }}</span>
    }
    @if (analysisRegenerationCount + revisionRegenerationCount > 0) {
      <span class="ml-1 summary-regenerate">{{ 'modals.survey-report.summary-regenerate' | translate: { analyses: analysisRegenerationCount, revisions: revisionRegenerationCount } }}</span>
    }
  </div>
</div>

<div class="buttons-container container mb-3 d-flex justify-content-between align-items-center">
  <button mat-button type="button" (click)="resetOptions()">{{ 'modals.survey-report.reset' | translate }}</button>
  <div>
    <button mat-stroked-button class="text-dark-purple border-purple mr-2" type="button" (click)="cancel()">{{ 'generic.buttons.cancel-and-back' | translate }}</button>
    <button mat-raised-button color="primary" type="button" [disabled]="!canConfirm" (click)="confirm()">
      {{ 'modals.survey-report.confirm' | translate }}
    </button>
  </div>
</div>
